import api from "../services/api";
import { tokenRenewed } from "../store/slices/authSlice";

// Sliding sessions: a login token is valid for 30 minutes (backend
// jwt.expiration), and while the person is using the app it's renewed
// (POST /api/auth/refresh). Someone who does nothing for 30 minutes is signed
// out at their next request, as before.
//
// "Using" means input, not requests: run pages poll the backend every few
// seconds on their own, which must not keep an idle session open. Steps
// arriving from the recorder count too: while recording, the person works in
// the recording window and doesn't touch this tab.

const CHECK_MS = 60 * 1000;
// Renew a token once it's this old, if there was activity since it was issued:
// the session then lasts 30 to 35 minutes after the last activity
const RENEW_AFTER_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart"];
// What the extension's bridge posts to this page while recording or replaying
const RECORDER_MESSAGES = new Set([
	"STEP_RECORDED",
	"REPLAY_PROGRESS",
	"RECORDING_STARTED",
	"ELEMENT_PICKED",
	"RECORDING_STOPPED",
]);

let lastActivity = Date.now(); // opening the app is activity
let lastCheck = 0;
let renewing = false;

// { iat, exp } in ms, or null for a missing or unreadable token
function tokenTimes(token) {
	try {
		const payload = JSON.parse(
			atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
		);
		return { iat: payload.iat * 1000, exp: payload.exp * 1000 };
	} catch {
		return null;
	}
}

async function check(store) {
	lastCheck = Date.now();
	const token = localStorage.getItem("token");
	const times = token && tokenTimes(token);
	if (!times || renewing) return;
	const now = Date.now();
	// Expired: the next request signs the person out (api.js)
	if (times.exp <= now) return;
	// Nothing done since this token was issued (here or in another tab)
	if (lastActivity <= times.iat) return;
	if (now - times.iat < RENEW_AFTER_MS) return;

	renewing = true;
	try {
		const { data } = await api.post("/auth/refresh");
		// Signed out or signed in again meanwhile: leave that token alone
		if (data?.accessToken && localStorage.getItem("token") === token) {
			store.dispatch(tokenRenewed(data.accessToken));
		}
	} catch {
		// Retried at the next check; a refused renewal (expired, blocked
		// account) ends the session at the next request anyway
	} finally {
		renewing = false;
	}
}

export function startSessionKeeper(store) {
	const active = () => {
		lastActivity = Date.now();
		// Back after a long pause: renew now rather than at the next minute,
		// in case the token is about to run out
		if (Date.now() - lastCheck > 15 * 1000) check(store);
	};
	ACTIVITY_EVENTS.forEach((type) =>
		window.addEventListener(type, active, { capture: true, passive: true }),
	);
	window.addEventListener("message", (event) => {
		if (
			event.source === window &&
			event.origin === window.location.origin &&
			RECORDER_MESSAGES.has(event.data?.action)
		) {
			active();
		}
	});
	setInterval(() => check(store), CHECK_MS);
}
